import { Component, OnInit, computed, inject, input, signal, viewChild } from '@angular/core';
import { SafeResourceUrl } from '@angular/platform-browser';
import { ColDef, GridOptions } from 'ag-grid-community';
import { forkJoin, of } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
import { PanModalComponent } from '../../../components/pan-modal/pan-modal.component';
import { tryGetErrorMessage } from '../../../error.utils';
import { defaultTableDefOptions } from '../../../helpers';
import { BranchService } from '../../../service/branch.service';
import { LocationObjectService } from '../../../service/location-object.service';
import { LocationService } from '../../../service/location.service';
import { NotifyService } from '../../../service/notify.service';
import { LocationCamera, LocationResponse } from '../../../types';
import { AssociateLocationObjectsRequest, LocationObject } from '../../../types/location-object';

import { CellLocationEditTerminalActionsComponent } from '../bl-management-location/cell-location-edit-terminal-actions/cell-location-edit-terminal-actions.component';

@Component({
  selector: 'app-camera-edit',
  templateUrl: './camera-edit.component.html',
  styleUrls: ['./camera-edit.component.scss'],
  standalone: false,
})
export class CameraEditComponent implements OnInit {
  private branchService = inject(BranchService);
  private locationService = inject(LocationService);
  private objectService = inject(LocationObjectService);
  private notifyService = inject(NotifyService);

  public locationId = input.required<number>();
  public locationCameraId = input.required<string>();

  public isLoadingData = signal<boolean>(false);
  public disableActions = signal<boolean>(false);

  public locationData = signal<LocationResponse | undefined>(undefined);
  public cameraData = signal<LocationCamera | undefined>(undefined);

  public cameraCardSubtitle = computed(() => {
    const locationData = this.locationData();
    const cameraData = this.cameraData();
    if (!locationData || !cameraData) return '';

    return `${cameraData.camera_name} (${locationData.timezone ?? 'No Timezone Set'})`;
  });

  public connectTerminalModal = viewChild<PanModalComponent>('connectTerminalModal');
  public allTerminals = signal<LocationObject<'Terminal'>[]>([]);
  public availableTerminals = computed(() => {
    const connectedTerminalIds = new Set(this.connectedTerminals().map((terminal) => terminal.id));
    return this.allTerminals()
      .filter((terminal) => !connectedTerminalIds.has(terminal.id))
      .map((t) => ({
        ...t,
        fullName: `${t.attributes.name} (ID: ${t.attributes.provider_terminal_id})`,
      }));
  });
  public connectedTerminals = signal<LocationObject<'Terminal'>[]>([]);
  public selectedTerminal = signal<LocationObject<'Terminal'> | undefined>(undefined);
  public viewCameraUrl = signal<SafeResourceUrl>(undefined);

  public readonly terminalColumns: ColDef<LocationObject<'Terminal'>>[] = [
    {
      headerName: 'Terminal Name',
      cellDataType: 'text',
      minWidth: 200,
      maxWidth: 400,
      filter: 'agTextColumnFilter',
      sortable: true,
      sort: 'asc',
      valueGetter: (params) => params.data.attributes.name,
    },
    {
      headerName: 'Terminal ID',
      cellDataType: 'text',
      filter: 'agTextColumnFilter',
      sortable: true,
      sort: 'asc',
      valueGetter: (params) => params.data.attributes.provider_terminal_id,
    },
    {
      headerName: '',
      suppressColumnsToolPanel: true,
      suppressKeyboardEvent: () => true,
      sortable: false,
      width: 210,
      maxWidth: 210,
      cellClass: ['inline-flex', 'items-center', 'justify-end', 'gap-2'],
      cellRenderer: CellLocationEditTerminalActionsComponent,
      cellRendererParams: {
        deleteCallback: (item: LocationObject<'Terminal'>) => {
          this.disconnectTerminal(item);
        },
      },
    },
  ];

  public readonly terminalTable: GridOptions<LocationObject<'Terminal'>> = {
    ...defaultTableDefOptions(),
  };

  ngOnInit() {
    this.loadData();
  }

  private loadData() {
    this.isLoadingData.set(true);
    forkJoin({
      location: this.locationService.getLocation({ locationId: this.locationId() }),
      camera: this.locationService.getLocationCameraById({
        locationId: this.locationId(),
        locationCameraId: this.locationCameraId(),
        options: { include_associations: true },
      }),
      terminals: this.objectService.getLocationObjectsOfType(this.locationId(), 'Terminal'),
    })
      .pipe(
        map(({ location, camera, terminals }) => ({
          location: location,
          camera: camera,
          terminals: terminals?.items ?? [],
        })),
        catchError(() =>
          of({
            location: undefined as LocationResponse | undefined,
            camera: undefined as LocationCamera | undefined,
            terminals: [] as LocationObject<'Terminal'>[],
          }),
        ),
        finalize(() => this.isLoadingData.set(false)),
      )
      .subscribe(({ location, camera, terminals }) => {
        if (!location || !camera) return;

        this.allTerminals.set(terminals);
        this.locationData.set(location);
        this.cameraData.set(camera);
        const terminalConnections = camera.associations
          .filter((conn) => conn.type === 'CameraToTerminal')
          .map((conn) => conn.object2 as LocationObject<'Terminal'>);
        this.connectedTerminals.set(terminalConnections);
      });
  }

  public connectCameraToTerminal() {
    if (!this.locationCameraId() || this.disableActions() || !this.selectedTerminal()) return;
    const selectedTerminal = this.selectedTerminal();
    this.disableActions.set(true);
    this.locationService
      .associateCameraWithObject({
        locationId: this.locationId(),
        locationCameraId: this.locationCameraId(),
        payload: {
          type: 'CameraToTerminal',
          to_location_object_id: selectedTerminal.id,
          attributes: {},
        } satisfies AssociateLocationObjectsRequest<'CameraToTerminal'>,
      })
      .pipe(finalize(() => this.disableActions.set(false)))
      .subscribe({
        next: () => {
          this.notifyService.notify('Successfully connected terminal');
          this.connectedTerminals.update((curr) => [...curr, selectedTerminal]);
        },
        error: (err) => {
          this.notifyService.notify(tryGetErrorMessage(err));
        },
        complete: () => {
          this.connectTerminalModal()?.hide();
        },
      });
  }

  public disconnectTerminal(terminal: LocationObject<'Terminal'>) {
    if (this.disableActions()) return;

    this.disableActions.set(true);
    const terminalToDisconnect = this.connectedTerminals().find((t) => t.id === terminal.id);
    if (!terminalToDisconnect) return;

    this.locationService
      .deleteCameraAssociation({
        locationId: this.locationData().id,
        locationCameraId: this.cameraData().id,
        atype: 'CameraToTerminal',
        objectId2: terminalToDisconnect.id,
      })
      .pipe(finalize(() => this.disableActions.set(false)))
      .subscribe({
        next: () => {
          this.notifyService.notify('Successfully removed terminal');
          this.connectedTerminals.update((curr) => curr.filter((t) => t.id !== terminalToDisconnect.id));
        },
        error: (err) => {
          this.notifyService.notify(tryGetErrorMessage(err));
        },
      });
  }

  public loadCameraSnapshot() {
    this.unloadViewCamera();
    this.branchService
      .getCameraSnapshot(this.locationData().branch_id, this.cameraData().provider_branch_id, this.cameraData().provider_camera_id)
      .subscribe({
        next: (blob) => {
          this.viewCameraUrl.set(URL.createObjectURL(blob));
        },
        error: () => this.viewCameraUrl.set('error'),
      });
  }

  public unloadViewCamera() {
    this.viewCameraUrl.set(undefined);
  }

  public unsetSelectedTerminal() {
    this.selectedTerminal.set(undefined);
  }
}
